import merge from 'lodash/merge.js'
import { type TTlds, type BaseJsonConfiguration } from './types.js'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Cypress: any
  }
}

type BuildDomainUtilsProps<TSupportedTlds extends TTlds> = {
  supportedTlds: TSupportedTlds
  defaultTld: TSupportedTlds[number]
  tldOverride?: string
  frontendDomain: string
}

type BuildConfigForTldProps<
  TSupportedTlds extends TTlds,
  TAppConfiguration,
  TJsonConfig extends BaseJsonConfiguration<TSupportedTlds, TAppConfiguration>,
> = {
  config: TJsonConfig
  tld: keyof TJsonConfig
}

export function buildDomainUtils<TSupportedTlds extends TTlds>({
  supportedTlds,
  defaultTld,
  tldOverride,
  frontendDomain,
}: BuildDomainUtilsProps<TSupportedTlds>): {
  getTopLevelDomain: (hostname?: string) => TSupportedTlds[number]
  forTopLevelDomain: <T>(values: Record<TSupportedTlds[number], T>, tld?: TSupportedTlds[number]) => T
  buildConfigForTld: <TAppConfiguration, TJsonConfig extends BaseJsonConfiguration<TSupportedTlds, TAppConfiguration>>(
    props: BuildConfigForTldProps<TSupportedTlds, TAppConfiguration, TJsonConfig>
  ) => TAppConfiguration
} {
  const tldOverrideKey = 'tldOverride'
  // eslint-disable-next-line n/no-unsupported-features/node-builtins
  const overrideTldValue = (window?.sessionStorage?.getItem(tldOverrideKey) || tldOverride)?.toLowerCase()

  if (overrideTldValue || window.Cypress) {
    document.cookie = `X-FRONTEND-HOST=${frontendDomain}.${String(overrideTldValue)}; path=/;` // Forward to our backends, for development and testing purposes
  }

  //
  // Determine toplevel domain
  //
  function getTopLevelDomain(hostname: string = window.location.hostname): TSupportedTlds[number] {
    if (overrideTldValue) {
      return overrideTldValue as TSupportedTlds[number]
    }

    const previewDomain = 'test.cl1.intra.dhlparcel.io'

    const host = hostname.toLowerCase()
    const tld = host.endsWith(previewDomain) // For example: preview-1-my-nl.cl1.intra.dhlparcel.io
      ? host.replace(`.${previewDomain}`, '').split('-').at(-1)
      : host.slice(host.lastIndexOf('.') + 1)

    return supportedTlds.find((supportedTld) => tld === supportedTld) ?? defaultTld
  }

  //
  // Get a value based on the tld
  //
  function forTopLevelDomain<T>(values: Record<TSupportedTlds[number], T>, tld?: TSupportedTlds[number]): T {
    const tldToUse = tld || getTopLevelDomain()
    return values[tldToUse] ?? values[defaultTld]
  }

  function buildConfigForTld<
    TAppConfiguration,
    TJsonConfig extends BaseJsonConfiguration<TSupportedTlds, TAppConfiguration>,
  >({ config, tld }: BuildConfigForTldProps<TSupportedTlds, TAppConfiguration, TJsonConfig>): TAppConfiguration {
    const defaultConfig = config.default
    return merge({}, defaultConfig, config[tld]) as TAppConfiguration
  }

  return {
    getTopLevelDomain,
    forTopLevelDomain,
    buildConfigForTld,
  }
}
